import {
  Tkv2CommentaryAttachmentBody,
  Tkv2Property,
  allocatePairedCostProperties,
  expected,
  isDefined,
  noop,
} from '@aloft/toolkit-shared';

import { getFilteredHousingTrendsData } from '../../modules/marketConditions/utils/getFilteredHousingTrendsData.ts';
import { pairedCostAdjustmentSummaryPropsFromOrderContext } from '../../modules/summaryV2/PairedCostAdjustmentSummary/utils/pairedCostAdjustmentSummaryPropsFromOrderContext.ts';
import {
  BreakdownCategory,
  ExportableCommentary,
  PropertyMetadata,
  Tkv2PairedCostSummaryInterfaceV2,
} from '../../modules/summaryV2/types.ts';
import { tkv2OrderContextCompsToMarketConditionsComps } from '../../utils/tkv2OrderContextCompsToMarketConditionsComps.ts';
import { tkv2OrderContextToMarketConditionsState } from '../../utils/tkv2OrderContextToMarketConditionsState.ts';
import { usePublicRecordsForOrder } from '../hooks/usePublicRecordsForOrder.ts';
import { Tkv2OrderContext } from '../types.ts';

type Tkv2CommentaryTextField = NonNullable<
  {
    [K in keyof Tkv2CommentaryAttachmentBody]: Tkv2CommentaryAttachmentBody[K] extends
      | string
      | undefined
      ? K
      : never;
  }[keyof Tkv2CommentaryAttachmentBody]
>;

type Tkv2CommentaryBooleanField = NonNullable<
  {
    [K in keyof Tkv2CommentaryAttachmentBody]: Tkv2CommentaryAttachmentBody[K] extends
      | boolean
      | undefined
      ? K
      : never;
  }[keyof Tkv2CommentaryAttachmentBody]
>;

export const useTkv2PairedCostSummaryDataV2 = (
  context: Tkv2OrderContext | null,
  accountID: string,
): Tkv2PairedCostSummaryInterfaceV2 | null => {
  const publicRecords = usePublicRecordsForOrder();

  if (!context) return null;

  const {
    commentaries,
    compSequence,
    comps,
    marketConditions,
    order,
    subject,
    updateCommentaries,
    pairedCostResults,
    pairedCostSettings,
    isPairedCostSettingsLoading,
    savePairedCostSettings,
    pairedCostVersion,
    setPairedCostVersion,
    orderID,
  } = context;

  const exportableCommentaries = getExportableCommentaries(commentaries, updateCommentaries);

  const effectiveDate = expected(order.input?.effectiveDate);

  const compPropTypes = compSequence.map((compID) => expected(comps[compID]).getPropertyType());
  const propertyTypes = [subject.body.getPropertyType(), ...compPropTypes].filter(isDefined);

  const { dateOfSaleAdjustment } = marketConditions.cachedState;
  const trendsData =
    (dateOfSaleAdjustment.selectedMethod === 'zillow' &&
      dateOfSaleAdjustment.methods?.zillow?.trends?.[0]?.data) ||
    (dateOfSaleAdjustment.selectedMethod === 'mlsData' &&
      dateOfSaleAdjustment.methods?.mlsData?.trends?.[0]?.data) ||
    undefined;

  const analysisPeriodMonths = pairedCostVersion !== 'legacy' ? 12 : undefined;

  const filteredHousingTrendsData = getFilteredHousingTrendsData(
    dateOfSaleAdjustment.selectedMethod === 'zillow',
    trendsData,
    effectiveDate,
    tkv2OrderContextCompsToMarketConditionsComps(comps, marketConditions),
    compSequence,
    analysisPeriodMonths,
  );

  const orderInput = order.input;

  const propertyType = expected(subject.body.getPropertyType() ?? orderInput?.propertyType);
  const reportJSON = allocatePairedCostProperties({
    pairedCostResults,
    subject: subject.body,
    comps,
    compSequence,
    propertyType,
  });

  const marketConditionsState = tkv2OrderContextToMarketConditionsState({
    compSequence,
    comps,
    subject,
    marketConditions,
    commentaries,
    order,
  });

  return {
    compSequence,
    subject: subject.body,
    comps,
    compMetadata: Object.fromEntries(
      compSequence.map((compID) => [compID, getPropertyMetadata(comps[compID])]),
    ),
    exportableCommentaries,
    orderInput,
    pairedCostSettings,
    isPairedCostSettingsLoading,
    propertyTypes,
    reportJSON,
    results: pairedCostResults,
    savePairedCostSettings,
    subjectMetadata: getPropertyMetadata(subject.body),
    marketConditions: marketConditionsState,
    housingTrends: filteredHousingTrendsData
      ? {
          filteredHousingTrendsData,
          postalCode: trendsData?.zip,
        }
      : undefined,
    orderID,
    accountID,
    pairedCostVersion,
    setPairedCostVersion,

    adjustmentSummaryProps: pairedCostAdjustmentSummaryPropsFromOrderContext(context),
    publicRecords,
  };
};

const getExportableCommentaryFunc = ({
  commentaries,
  updateCommentaries,
}: {
  commentaries: Tkv2CommentaryAttachmentBody | undefined;
  updateCommentaries: (commentaries: Tkv2CommentaryAttachmentBody) => void;
}): ((fields: {
  commentaryField: Tkv2CommentaryTextField;
  isOmitField: Tkv2CommentaryBooleanField;
}) => ExportableCommentary) => {
  const updateCommentary = (field: Tkv2CommentaryTextField) => (commentary: string) =>
    updateCommentaries({ ...commentaries, [field]: commentary });

  const toggleOmitFromExport = (field: Tkv2CommentaryBooleanField) => (val: boolean) =>
    updateCommentaries({ ...commentaries, [field]: val });

  return ({
    commentaryField,
    isOmitField,
  }: {
    commentaryField: Tkv2CommentaryTextField;
    isOmitField: Tkv2CommentaryBooleanField;
  }) => ({
    commentary: commentaries?.[commentaryField] ?? '',
    updateCommentary: updateCommentary(commentaryField),
    omitFromExport: commentaries?.[isOmitField],
    toggleOmitFromExport: toggleOmitFromExport(isOmitField),
  });
};

const getExportableCommentaries = (
  commentaries: Tkv2CommentaryAttachmentBody | undefined,
  updateCommentaries: (commentaries: Tkv2CommentaryAttachmentBody) => void,
): {
  [key in BreakdownCategory | 'costApproach']: ExportableCommentary;
} => {
  const getExportableCommentary = getExportableCommentaryFunc({
    commentaries,
    updateCommentaries,
  });

  return {
    basement: getExportableCommentary({
      commentaryField: 'basementAdjustment',
      isOmitField: 'isBasementCommentaryOmit',
    }),
    attic: getExportableCommentary({
      commentaryField: 'atticAdjustment',
      isOmitField: 'isAtticCommentaryOmit',
    }),
    fireplace: {
      commentary: '',
      toggleOmitFromExport: () => {
        console.warn('fireplace not toggle-able');
      },
      omitFromExport: true,
      updateCommentary: () => {
        console.warn('fireplace not updatable in tkv2');
      },
    },
    pool: getExportableCommentary({
      commentaryField: 'poolAdjustment',
      isOmitField: 'isPoolCommentaryOmit',
    }),
    adu: getExportableCommentary({
      commentaryField: 'guestHouseAdjustment',
      isOmitField: 'isGuestHouseCommentaryOmit',
    }),
    shed: getExportableCommentary({
      commentaryField: 'shedAdjustment',
      isOmitField: 'isShedCommentaryOmit',
    }),
    workshop: getExportableCommentary({
      commentaryField: 'workshopAdjustment',
      isOmitField: 'isWorkshopCommentaryOmit',
    }),
    bonusRoom: getExportableCommentary({
      commentaryField: 'bonusRoomAdjustment',
      isOmitField: 'isBonusRoomCommentaryOmit',
    }),
    barn: getExportableCommentary({
      commentaryField: 'barnAdjustment',
      isOmitField: 'isBarnCommentaryOmit',
    }),
    manufactured: getExportableCommentary({
      commentaryField: 'manufacturedDwellingAdjustment',
      isOmitField: 'isManufacturedDwellingCommentaryOmit',
    }),
    mainDwelling: getExportableCommentary({
      commentaryField: 'glaAdjustment',
      isOmitField: 'isGlaCommentaryOmit',
    }),
    belowGradeANSI: getExportableCommentary({
      commentaryField: 'belowGradeAdjustment',
      isOmitField: 'isBelowGradeCommentaryOmit',
    }),
    manual: getExportableCommentary({
      commentaryField: 'manualCostAdjustment',
      isOmitField: 'isManualCostCommentaryOmit',
    }),
    market: getExportableCommentary({
      commentaryField: 'marketConditions',
      isOmitField: 'isMarketConditionsCommentaryOmit',
    }),
    quality: getExportableCommentary({
      commentaryField: 'qualityAdjustment',
      isOmitField: 'isQualityCommentaryOmit',
    }),
    condition: getExportableCommentary({
      commentaryField: 'conditionAdjustment',
      isOmitField: 'isConditionCommentaryOmit',
    }),
    garageCarport: getExportableCommentary({
      commentaryField: 'parkingAdjustment',
      isOmitField: 'isParkingCommentaryOmit',
    }),
    porchPatioDeck: getExportableCommentary({
      commentaryField: 'patioDeckAdjustment',
      isOmitField: 'isPatioDeckCommentaryOmit',
    }),
    site: getExportableCommentary({
      commentaryField: 'siteAdjustments',
      isOmitField: 'isSiteCommentaryOmit',
    }),
    allImprovements: getExportableCommentary({
      commentaryField: 'improvementsAdjustments',
      isOmitField: 'isImprovementsCommentaryOmit',
    }),
    costApproach: getExportableCommentary({
      commentaryField: 'costApproach',
      isOmitField: 'isCostApproachCommentaryOmit',
    }),
    bathAboveGrade: getExportableCommentary({
      commentaryField: 'glaAdjustment',
      isOmitField: 'isGlaCommentaryOmit',
    }),
    bathBelowGrade: getExportableCommentary({
      commentaryField: 'glaAdjustment',
      isOmitField: 'isGlaCommentaryOmit',
    }),
    locationView: getExportableCommentary({
      commentaryField: 'siteAdjustments',
      isOmitField: 'isSiteCommentaryOmit',
    }),
    foundation: {
      commentary: '',
      updateCommentary: () => '',
      omitFromExport: true,
      toggleOmitFromExport: noop,
    },
    units: {
      commentary: '',
      updateCommentary: () => '',
      omitFromExport: true,
      toggleOmitFromExport: noop,
    },
  };
};

const getPropertyMetadata = (property: Tkv2Property | undefined): PropertyMetadata => ({
  address: property?.getStructuredData('address')?.AddressLineText ?? 'Unk',
  salePrice: property?.getStructuredData('salePrice')?.toString(true) ?? '',
});
